import { BigNumber, FixedNumber } from '@ethersproject/bignumber'
import { ChainId, FactoryVersion, WNATIVE } from '@nominex/nomiswap-sdk'
import { QUOTE_TOKENS } from 'config/constants/exchange'
import { FIXED_0, FIXED_1, FIXED_2 } from 'config/constants/fixedNumber'
import { SerializedFarm } from 'state/types'

const evmNativeStableLpMap = {
  [ChainId.MATIC]: {
    address: '',
  },
  [ChainId.ETHEREUM]: {
    address: '',
  },
  [ChainId.BSC]: {
    address: '0xe2Bbf54Dc0ccDD0Cf6270f2aF2f62FF79903Bb27',
  },
}

const BIG_10 = BigNumber.from(10)

const equalsIgnoreCase = (a?: string, b?: string) => {
  if (!a || !b) {
    return false
  }

  return a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0
}

const getDecimalMultiplier = (decimals: number): BigNumber => BIG_10.pow(decimals)

// Find BUSD price for token
// either via direct calculation if farm is X-BNB or X-BUSD
// or via quoteTokenFarm which is quoteToken-BNB or quoteToken-BUSD farm
export const getFarmBaseTokenPrice = (
  farm: SerializedFarm,
  quoteTokenFarm: SerializedFarm,
  nativePriceUSD: FixedNumber,
  wNative: string,
  stableTokens: string[],
  quoteTokenInBusd,
): FixedNumber => {
  const hasTokenPriceVsQuote = Boolean(farm.tokenPriceVsQuote)

  if (stableTokens.includes(farm.quoteToken.symbol)) {
    return hasTokenPriceVsQuote ? FixedNumber.from(farm.tokenPriceVsQuote) : FIXED_1
  }

  if (farm.quoteToken.symbol === wNative) {
    return hasTokenPriceVsQuote ? nativePriceUSD.mulUnsafe(FixedNumber.from(farm.tokenPriceVsQuote)) : FIXED_1
  }

  // We can only calculate profits without a quoteTokenFarm for BUSD/BNB farms
  if (!quoteTokenFarm) {
    return FIXED_0
  }

  // Possible alternative farm quoteTokens:
  // UST (i.e. MIR-UST), pBTC (i.e. PNT-pBTC), BTCB (i.e. bBADGER-BTCB), ETH (i.e. SUSHI-ETH)
  // If the farm's quote token isn't BUSD or WBNB, we then use the quote token, of the original farm's quote token
  // i.e. for farm PNT - pBTC we use the pBTC farm's quote token - BNB, (pBTC - BNB)
  // from the BNB - pBTC price, we can calculate the PNT - BUSD price
  if (quoteTokenFarm.quoteToken.symbol === wNative || stableTokens.includes(quoteTokenFarm.quoteToken.symbol)) {
    return hasTokenPriceVsQuote && quoteTokenInBusd
      ? FixedNumber.from(farm.tokenPriceVsQuote).mulUnsafe(quoteTokenInBusd)
      : FIXED_1
  }

  // Catch in case token does not have immediate or once-removed BUSD/WBNB quoteToken
  return FIXED_0
}

export const getFarmQuoteTokenPrice = (
  farm: SerializedFarm,
  quoteTokenFarm: SerializedFarm,
  nativePriceUSD: FixedNumber,
  wNative: string,
  stableTokens: string[],
): FixedNumber => {
  if (stableTokens.includes(farm.quoteToken.symbol)) {
    return FIXED_1
  }

  if (farm.quoteToken.symbol === wNative) {
    return nativePriceUSD
  }

  if (!quoteTokenFarm) {
    return FIXED_0
  }

  if (quoteTokenFarm.quoteToken.symbol === wNative) {
    return quoteTokenFarm.tokenPriceVsQuote
      ? nativePriceUSD.mulUnsafe(FixedNumber.from(quoteTokenFarm.tokenPriceVsQuote))
      : FIXED_0
  }

  if (stableTokens.includes(quoteTokenFarm.quoteToken.symbol)) {
    return quoteTokenFarm.tokenPriceVsQuote ? FixedNumber.from(quoteTokenFarm.tokenPriceVsQuote) : FIXED_0
  }

  return FIXED_0
}

const getFarmFromTokenAddress = (
  farms: SerializedFarm[],
  tokenAddress: string,
  preferredQuoteTokens?: string[],
): SerializedFarm => {
  const farmsWithTokenSymbol = farms.filter((farm) => equalsIgnoreCase(farm.token.address, tokenAddress))
  const preferredFarm = farmsWithTokenSymbol.find((farm) =>
    preferredQuoteTokens.some((quoteToken) => farm.quoteToken.symbol === quoteToken),
  )

  return preferredFarm || farms[0]
}

export const getLpTokenPrice = (
  lpTotalSupply: FixedNumber,
  lpTotalInQuoteToken: FixedNumber,
  tokenAmountTotal: FixedNumber,
  tokenPriceBusd: FixedNumber,
  decimals: number,
) => {
  if (lpTotalSupply.isZero() || lpTotalInQuoteToken.isZero()) {
    return FIXED_0
  }

  // Total value of base token in LP
  const valueOfBaseTokenInFarm = tokenPriceBusd.mulUnsafe(tokenAmountTotal)
  // Double it to get overall value in LP
  const overallValueOfAllTokensInFarm = valueOfBaseTokenInFarm.mulUnsafe(FIXED_2)
  // Divide total value of all tokens, by the number of LP tokens
  const totalLpTokens = lpTotalSupply.divUnsafe(FixedNumber.from(getDecimalMultiplier(decimals)))

  return overallValueOfAllTokensInFarm.divUnsafe(totalLpTokens)
}

export const getStableSwapLpTokenPrice = (
  lpTotalSupply: FixedNumber,
  tokenAmountTotal: FixedNumber,
  quoteTokenAmountTotal: FixedNumber,
  decimals: number,
) => {
  if (lpTotalSupply.isZero()) {
    return FIXED_0
  }

  const liquidity = tokenAmountTotal.addUnsafe(quoteTokenAmountTotal)
  const totalLpTokens = lpTotalSupply.divUnsafe(FixedNumber.from(getDecimalMultiplier(decimals)))

  return liquidity.divUnsafe(totalLpTokens)
}

export const getFarmPrices = (farms: SerializedFarm[], chainId: ChainId) => {
  const wNative = WNATIVE[chainId]
  const stableTokenSymbols = QUOTE_TOKENS[chainId].map((token) => token.symbol) ?? []
  const nativeStableFarm = farms.find((farm) => equalsIgnoreCase(farm.lpAddress, evmNativeStableLpMap[chainId].address))
  const isNativeFirst = nativeStableFarm?.token.symbol === wNative.symbol

  const nativePriceUSD =
    nativeStableFarm && Number(nativeStableFarm?.tokenPriceVsQuote) !== 0
      ? isNativeFirst
        ? FixedNumber.from(nativeStableFarm.tokenPriceVsQuote)
        : FIXED_1.divUnsafe(FixedNumber.from(nativeStableFarm.tokenPriceVsQuote))
      : FIXED_0

  return farms.map((farm) => {
    const quoteTokenFarm = getFarmFromTokenAddress(farms, farm.quoteToken.address, [
      wNative.symbol,
      ...stableTokenSymbols,
    ])

    const quoteTokenPriceBusd = getFarmQuoteTokenPrice(
      farm,
      quoteTokenFarm,
      nativePriceUSD,
      wNative.symbol,
      stableTokenSymbols,
    )

    const tokenPriceBusd = getFarmBaseTokenPrice(
      farm,
      quoteTokenFarm,
      nativePriceUSD,
      wNative.symbol,
      stableTokenSymbols,
      quoteTokenPriceBusd,
    )

    const lpTokenPrice =
      farm.version === FactoryVersion.V2
        ? getStableSwapLpTokenPrice(
            FixedNumber.from(farm.lpTotalSupply),
            FixedNumber.from(farm.tokenAmountTotal),
            FixedNumber.from(farm.quoteTokenAmountTotal),
            18,
          )
        : getLpTokenPrice(
            FixedNumber.from(farm.lpTotalSupply),
            FixedNumber.from(farm.lpTotalInQuoteToken),
            FixedNumber.from(farm.tokenAmountTotal),
            tokenPriceBusd,
            18,
          )

    return {
      ...farm,
      tokenPriceBusd: tokenPriceBusd.toString(),
      quoteTokenPriceBusd: quoteTokenPriceBusd.toString(),
      lpTokenPriceBusd: lpTokenPrice.toString(),
    }
  })
}
